import { useState, useEffect, useRef } from "react";
import { Play, Pause, Clock, Music } from "lucide-react";
import { ListObjectsV2Command, S3Client } from "@aws-sdk/client-s3";

const BUCKET_NAME = "jugendstunden"

const credentials = {
    accessKeyId: "a3LKkEJ9DbgESrTD",
    secretAccessKey: "f2bD8QjXI16HUUlq2pNByaFdANtXqiWLi8tj9Fod"
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'Date unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

export default function LastAudios() {
    const [audioFiles, setAudioFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [audioErrors, setAudioErrors] = useState({});
    const audioRefs = useRef({});

    // Fetch audio files from server
    const fetchAudioFiles = async () => {
        try {
            const s3Client = new S3Client({
                endpoint: "https://s3.tebi.io",
                credentials: credentials,
                region: "global"
            });

            const listCommand = new ListObjectsV2Command({
                Bucket: BUCKET_NAME,
                MaxKeys: 100
            });

            const response = await s3Client.send(listCommand);
            console.log(response);

            const files = (response.Contents ?? [])
                .filter(obj => obj.Key?.toLowerCase().endsWith('.mp3'))
                .map(obj => {
                    const base = obj.Key.split("/").pop() || obj.Key;
                    const displayName = base.replace(/\.[^.]+$/, "");
                    return {
                        filename: obj.Key,
                        url: `https://${BUCKET_NAME}.s3.tebi.io/${obj.Key}`,
                        displayName,
                        lastModified: obj.LastModified
                    };
                });

            setAudioFiles(files);
            setError(null);
        } catch (err) {
            console.error('Error fetching audio files:', err);
            setError('Failed to load audio files');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAudioFiles();
    }, []);

    const togglePlay = async (filename) => {
        const audio = audioRefs.current[filename];
        if (!audio) return;

        // Check if this audio file has previously failed to load
        if (audioErrors[filename]) {
            console.error(`Audio file ${filename} has loading errors`);
            return;
        }

        if (currentlyPlaying === filename) {
            audio.pause();
            setCurrentlyPlaying(null);
        } else {
            // Pause all other audio elements
            Object.values(audioRefs.current).forEach(a => a.pause());
            
            try {
                // Check if audio can load before playing
                if (audio.readyState === 0) {
                    audio.load();
                }
                await audio.play();
                setCurrentlyPlaying(filename);
            } catch (playError) {
                console.error(`Error playing audio ${filename}:`, playError);
                setAudioErrors(prev => ({
                    ...prev,
                    [filename]: `Failed to play: ${playError.message}`
                }));
            }
        }
    };

    const handleAudioEnded = (filename) => {
        if (currentlyPlaying === filename) {
            setCurrentlyPlaying(null);
        }
    };

    const handleAudioError = (filename, error) => {
        console.error(`Audio error for ${filename}:`, error);
        const errorMessage = error.target?.error ? 
            `Error code ${error.target.error.code}: ${getErrorMessage(error.target.error.code)}` :
            'Unknown audio error';
        
        setAudioErrors(prev => ({
            ...prev,
            [filename]: errorMessage
        }));
        
        if (currentlyPlaying === filename) {
            setCurrentlyPlaying(null);
        }
    };

    const getErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 1: return 'Media loading aborted';
            case 2: return 'Network error';
            case 3: return 'Media decoding error - unsupported format';
            case 4: return 'Media not supported';
            default: return 'Unknown error';
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-100 p-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600">Laden...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center min-h-[calc(100vh-4rem)] bg-gray-100 p-6">
            <h1 className="text-3xl font-bold mb-6 text-gray-800">Die letzten Jugendstunden</h1>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 max-w-md text-center">
                    {error}
                </div>
            )}
            
            {audioFiles.length === 0 ? (
                <div className="text-center max-w-md">
                    <Music className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <p className="text-xl text-gray-600 mb-2">Keine Audios gefunden</p>
                    <p className="text-gray-500">Die letzten Jugendstunden werden hier erscheinen.</p>
                </div>
            ) : (
                <div className="w-full max-w-2xl space-y-4">
                    {audioFiles.map((file, index) => (
                        <div 
                            key={file.filename}
                            className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3 flex-1">
                                    <Clock className="h-5 w-5 text-gray-400" />
                                    <div className="flex-1">
                                        <h3 className="font-medium text-gray-900">{file.displayName}</h3>
                                        <p className="text-sm text-gray-500">{formatDate(file.lastModified)}</p>
                                        {audioErrors[file.filename] && (
                                            <p className="text-xs text-red-500 mt-1">{audioErrors[file.filename]}</p>
                                        )}
                                    </div>
                                </div>
                                
                                <button
                                    onClick={() => togglePlay(file.filename)}
                                    disabled={audioErrors[file.filename]}
                                    className={`flex items-center justify-center w-12 h-12 text-white rounded-full transition-colors ${
                                        audioErrors[file.filename] 
                                            ? 'bg-gray-400 cursor-not-allowed' 
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {currentlyPlaying === file.filename ? (
                                        <Pause size={20} />
                                    ) : (
                                        <Play size={20} className="ml-0.5" />
                                    )}
                                </button>
                            </div>
                            
                            <audio
                                ref={el => audioRefs.current[file.filename] = el}
                                src={file.url}
                                onEnded={() => handleAudioEnded(file.filename)}
                                onError={(e) => handleAudioError(file.filename, e)}
                                onLoadStart={() => console.log(`Loading started for ${file.filename}`)}
                                onCanPlay={() => console.log(`Can play ${file.filename}`)}
                                preload="metadata"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}